import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { INITIATIVE_FIELDS } from "../domain/initiativeFields";
import ActivityChange from "./ActivityChange";
import TruncatedCell from "./TruncatedCell";
import WarningPopup from "./WarningPopup";

export default function ActivityView({ activity, visibleActivity, states }) {
  const {
    loading,
    error,
    loadingMore,
    loadMoreError,
    clearLoadMoreError,
    hasMore,
    loadMore,
  } = states;

  function getFieldLabel(entry) {
    if (
      entry.type === "deletion" ||
      entry.field === "created" ||
      entry.field === "archived"
    ) {
      return "—";
    }
    if (entry.field === "comment") return "Comment";
    return INITIATIVE_FIELDS[entry.field]?.label || entry.field;
  }

  const styles = {
    root: { p: 3 },
    tableContainer: { bgcolor: "background.tableHeader" },
    headerRow: {
      "& th": {
        fontWeight: 700,
        bgcolor: "background.tableHeader",
        color: "background.tableHeaderText",
        fontSize: "0.7rem",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
      },
    },
    tableBody: {
      bgcolor: "background.tableBody",
      "& td": { verticalAlign: "top" },
    },
    timeCell: { whiteSpace: "nowrap" },
    initiativeCell: {
      fontWeight: 600,
      maxWidth: 240,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    fieldCell: { whiteSpace: "nowrap", color: "background.textMuted" },
    changeCell: { maxWidth: 560, wordBreak: "break-word" },
    changedByCell: { whiteSpace: "nowrap" },
    loadingContainer: { display: "flex", justifyContent: "center", py: 8 },
    empty: { p: 3, display: "flex", justifyContent: "center" },
    emptyState: { color: "background.textMuted" },
    footer: { display: "flex", justifyContent: "center", py: 2 },
    endOfFeed: { color: "background.textMuted" },
    loadBtn: { textTransform: "none" },
  };

  if (loading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={styles.empty}>
        <Typography sx={styles.emptyState}>Failed to load activity.</Typography>
      </Box>
    );
  }

  if (visibleActivity.length === 0) {
    return (
      <Box sx={styles.empty}>
        <Typography sx={styles.emptyState}>
          {activity.length === 0
            ? "No activity yet."
            : "No entries match the selected filters."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      <TableContainer
        component={Paper}
        elevation={2}
        sx={styles.tableContainer}
      >
        <Table size="medium" stickyHeader>
          <TableHead>
            <TableRow sx={styles.headerRow}>
              <TableCell>Time</TableCell>
              <TableCell>Initiative</TableCell>
              <TableCell>Field</TableCell>
              <TableCell>Change</TableCell>
              <TableCell>Changed By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={styles.tableBody}>
            {visibleActivity.map((entry) => (
              <TableRow key={`${entry.type}-${entry.id}`}>
                <TableCell sx={styles.timeCell}>
                  {new Date(entry.occurredAt).toLocaleString()}
                </TableCell>
                <TruncatedCell
                  value={entry.initiativeTitle}
                  sx={styles.initiativeCell}
                />
                <TableCell sx={styles.fieldCell}>
                  {getFieldLabel(entry)}
                </TableCell>
                <TableCell sx={styles.changeCell}>
                  <ActivityChange entry={entry} />
                </TableCell>
                <TableCell sx={styles.changedByCell}>
                  {entry.changedBy}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={styles.footer}>
        {hasMore ? (
          <Button
            onClick={loadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            sx={styles.loadBtn}
          >
            {loadingMore ? "Loading..." : "Load More"}
          </Button>
        ) : (
          <Typography variant="body2" sx={styles.endOfFeed}>
            No more activity.
          </Typography>
        )}
      </Box>
      <WarningPopup message={loadMoreError} onClose={clearLoadMoreError} />
    </Box>
  );
}
